import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Badge,
  V2EmptyState,
  V2FormField,
  V2FormGrid,
  LoadingState,
  StatePanel,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { useFinanceBook } from './useFinanceBook.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type {
  TrialBalanceColumnAmounts,
  TrialBalanceColumnsResponse,
} from '../../lib/types/finance.js';

type ColumnCount = 2 | 4 | 6 | 8;

interface ColumnGroup {
  readonly title: string;
  readonly debit: keyof TrialBalanceColumnAmounts;
  readonly credit: keyof TrialBalanceColumnAmounts;
}

const OPENING: ColumnGroup = {
  title: 'مانده ابتدای دوره',
  debit: 'openingDebitRial',
  credit: 'openingCreditRial',
};
const PERIOD: ColumnGroup = {
  title: 'گردش طی دوره',
  debit: 'periodDebitRial',
  credit: 'periodCreditRial',
};
const CUMULATIVE: ColumnGroup = {
  title: 'جمع گردش',
  debit: 'cumulativeDebitRial',
  credit: 'cumulativeCreditRial',
};
const CLOSING: ColumnGroup = {
  title: 'مانده پایان دوره',
  debit: 'closingDebitRial',
  credit: 'closingCreditRial',
};

/** Which column pairs each classic Iranian trial-balance form shows. */
export const COLUMN_GROUPS: Record<ColumnCount, readonly ColumnGroup[]> = {
  2: [CLOSING],
  4: [PERIOD, CLOSING],
  6: [OPENING, PERIOD, CLOSING],
  8: [OPENING, PERIOD, CUMULATIVE, CLOSING],
};

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Finance.dc.html §11 REPORTS — تراز ۲/۴/۶/۸ ستونی (SOT §12.11)
 * archetype: filtered report table + totals row
 * status: PARITY — `GET .../finance/reports/trial-balance-columns`,
 *   posted-only. Every amount, the level roll-up and the "contrary to
 *   account nature" flag are server-computed; this page only chooses which
 *   column pairs to show.
 * state reference: loading · empty · permission_denied · network_error · validation
 */
export function TrialBalanceColumnsPage() {
  const book = useFinanceBook();
  const { unit, label } = useAmountDisplay();
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [columns, setColumns] = useState<ColumnCount>(8);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [level, setLevel] = useState('');

  const query = new URLSearchParams();
  if (fromDate) query.set('fromDate', fromDate);
  if (toDate) query.set('toDate', toDate);
  if (level) query.set('levelSequence', level);
  if (book.bookId) query.set('bookId', book.bookId);
  const report = useApiGet<TrialBalanceColumnsResponse>(
    slug && book.ready
      ? `${base}/api/v1/finance/reports/trial-balance-columns?${query.toString()}`
      : null,
    [slug, query.toString()],
  );

  if (!slug) return null;
  const groups = COLUMN_GROUPS[columns];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card>
        <V2CardHeader
          title="تراز چند ستونی"
          subtitle="۲، ۴، ۶ یا ۸ ستونی — فقط اسناد قطعی"
          actions={
            report.status === 'ready' ? (
              <Link
                className="sk-btn sk-btn--secondary sk-btn--sm"
                to={`${base}/finance/reports/trial-balance-columns/print?columns=${columns}&${query.toString()}`}
              >
                چاپ
              </Link>
            ) : null
          }
        />
        <div style={{ padding: '0 20px 18px' }}>
          <V2FormGrid>
            <V2FormField label="نوع تراز" width="medium">
              <select
                className="sk-select"
                value={columns}
                onChange={(e) => setColumns(Number(e.target.value) as ColumnCount)}
              >
                <option value={2}>۲ ستونی</option>
                <option value={4}>۴ ستونی</option>
                <option value={6}>۶ ستونی</option>
                <option value={8}>۸ ستونی</option>
              </select>
            </V2FormField>
            <V2FormField label="سطح حساب" width="medium">
              <select
                className="sk-select"
                value={level}
                onChange={(e) => setLevel(e.target.value)}
              >
                <option value="">حساب‌های قابل ثبت</option>
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>
                    سطح {n.toLocaleString('fa-IR')}
                  </option>
                ))}
              </select>
            </V2FormField>
            <V2FormField
              label="از تاریخ"
              width="medium"
              hint="مانده‌ی قبل از این تاریخ، افتتاحیه است"
            >
              <JalaliDateInput value={fromDate} onChange={setFromDate} />
            </V2FormField>
            <V2FormField label="تا تاریخ" width="medium">
              <JalaliDateInput value={toDate} onChange={setToDate} />
            </V2FormField>
          </V2FormGrid>
        </div>
      </V2Card>

      {report.status === 'loading' ? <LoadingState /> : null}
      {report.status === 'error' ? (
        <StatePanel
          variant={report.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={
            report.error.status === 403
              ? 'دسترسی ندارید'
              : report.error.status === 422
                ? 'بازهٔ تاریخ نامعتبر است'
                : 'گزارش قابل نمایش نیست'
          }
        />
      ) : null}
      {report.status === 'ready' && report.data.rows.length === 0 ? (
        <V2EmptyState icon="finance" title="سند قطعی در این بازه ثبت نشده" />
      ) : null}
      {report.status === 'ready' && report.data.rows.length > 0 ? (
        <V2Card>
          <V2CardHeader
            title={`تراز ${columns.toLocaleString('fa-IR')} ستونی`}
            actions={
              <V2Badge tone={report.data.balanced ? 'success' : 'danger'}>
                {report.data.balanced ? 'تراز است' : 'تراز نیست'}
              </V2Badge>
            }
          />
          <div className="sk-table-scroll">
            <table className="sk-table sk-table--compact">
              <thead>
                <tr>
                  <th rowSpan={2}>کد</th>
                  <th rowSpan={2}>نام حساب</th>
                  {groups.map((g) => (
                    <th key={g.title} colSpan={2} style={{ textAlign: 'center' }}>
                      {g.title} ({label})
                    </th>
                  ))}
                </tr>
                <tr>
                  {groups.flatMap((g) => [
                    <th key={`${g.title}-d`}>بدهکار</th>,
                    <th key={`${g.title}-c`}>بستانکار</th>,
                  ])}
                </tr>
              </thead>
              <tbody>
                {report.data.rows.map((row) => (
                  <tr key={row.accountRef}>
                    <td className="ltr sk-num">{row.accountRef}</td>
                    <td>
                      {row.accountName}
                      {row.contraryToNature ? (
                        <span
                          className="sk-tag"
                          style={{ marginInlineStart: 6, color: 'var(--sk-danger)', fontSize: 11 }}
                          title="مانده‌ی این حساب خلاف ماهیت تعریف‌شده‌ی آن است"
                        >
                          خلاف ماهیت
                        </span>
                      ) : null}
                    </td>
                    {groups.flatMap((g) => [
                      <td key={`${g.title}-d`} className="sk-num">
                        {formatRialFa(row[g.debit], unit)}
                      </td>,
                      <td key={`${g.title}-c`} className="sk-num">
                        {formatRialFa(row[g.credit], unit)}
                      </td>,
                    ])}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} style={{ fontWeight: 700 }}>
                    جمع
                  </td>
                  {groups.flatMap((g) => [
                    <td key={`${g.title}-d`} className="sk-num" style={{ fontWeight: 700 }}>
                      {formatRialFa(report.data.totals[g.debit], unit)}
                    </td>,
                    <td key={`${g.title}-c`} className="sk-num" style={{ fontWeight: 700 }}>
                      {formatRialFa(report.data.totals[g.credit], unit)}
                    </td>,
                  ])}
                </tr>
              </tfoot>
            </table>
          </div>
        </V2Card>
      ) : null}
    </div>
  );
}
